<div class="min-vh-100 bg-light">
  <app-header [page]="'Admin'"></app-header>

  <main class="container py-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h4 class="mb-0 fw-bold">Withdrawal Gateway Mappings</h4>
        <div class="text-muted small">Configure withdrawal gateway routing by currency and workflow</div>
      </div>

      <button class="btn btn-primary btn-sm" type="button" (click)="openCreateModal()">
        Add Mapping
      </button>
    </div>

    <div class="card shadow-sm border-0 mb-3">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-4">
            <label class="form-label">Currency</label>
            <select
              class="form-select"
              [(ngModel)]="filterCurrencyCode"
              [ngModelOptions]="{standalone: true}">
              <option value="">All currencies</option>
              <option *ngFor="let c of currencies" [value]="c.currencyCode">
                {{ c.currencyName }} ({{ c.currencyCode }})
              </option>
            </select>
          </div>

          <div class="col-md-4">
            <label class="form-label">Workflow</label>
            <select
              class="form-select"
              [(ngModel)]="filterWorkflow"
              [ngModelOptions]="{standalone: true}">
              <option value="">All</option>
              <option *ngFor="let item of workflowOptions" [value]="item">{{ item }}</option>
            </select>
          </div>

          <div class="col-md-4">
            <button class="btn btn-outline-secondary w-100" type="button" (click)="loadMappings()">
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="card shadow-sm border-0">
      <div class="card-body">
        <div *ngIf="loading" class="text-center py-4">
          <div class="spinner-border spinner-border-sm me-2"></div>
          Loading mappings...
        </div>

        <div class="table-responsive" *ngIf="!loading">
          <table class="table align-middle">
            <thead>
              <tr>
                <th>Currency</th>
                <th>Provider</th>
                <th>Gateway Config</th>
                <th>Workflow</th>
                <th>Method</th>
                <th>Country</th>
                <th>Priority</th>
                <th>Default</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              <tr *ngFor="let item of filteredMappings">
                <td class="fw-semibold">{{ item.currencyCode }}</td>
                <td>{{ item.providerName }}</td>
                <td>{{ item.gatewayConfigName }}</td>
                <td>{{ item.workflow }}</td>
                <td>{{ item.paymentMethod || '-' }}</td>
                <td>{{ item.countryCode || '-' }}</td>
                <td>{{ item.priority }}</td>
                <td>
                  <span class="badge rounded-pill" [ngClass]="boolBadgeClass(item.isDefault)">
                    {{ item.isDefault ? 'Default' : 'No' }}
                  </span>
                </td>
                <td>
                  <span class="badge rounded-pill" [ngClass]="badgeClass(item.isActive)">
                    {{ item.isActive ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td class="text-end">
                  <div class="d-flex gap-2 justify-content-end">
                    <button class="btn btn-outline-primary btn-sm" type="button" (click)="openEditModal(item)">
                      Edit
                    </button>

                    <button
                      class="btn btn-outline-secondary btn-sm"
                      type="button"
                      (click)="setDefault(item)"
                      [disabled]="item.isDefault">
                      Set Default
                    </button>

                    <button
                      class="btn btn-outline-warning btn-sm"
                      type="button"
                      (click)="toggleStatus(item)">
                      {{ item.isActive ? 'Disable' : 'Enable' }}
                    </button>
                  </div>
                </td>
              </tr>

              <tr *ngIf="!filteredMappings.length">
                <td colspan="10" class="text-center text-muted py-4">
                  No gateway mappings found.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div
      class="modal fade show"
      tabindex="-1"
      [ngStyle]="{ display: showModal ? 'block' : 'none' }"
      [class.d-block]="showModal"
      *ngIf="showModal">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingId ? 'Edit Withdrawal Gateway Mapping' : 'Create Withdrawal Gateway Mapping' }}
            </h5>
            <button type="button" class="btn-close" (click)="closeModal()"></button>
          </div>

          <form [formGroup]="form" (ngSubmit)="save()" novalidate>
            <div class="modal-body">
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label">Currency Code</label>
                  <select class="form-select" formControlName="currencyCode">
                    <option value="">Select currency</option>
                    <option *ngFor="let c of currencies" [value]="c.currencyCode">
                      {{ c.currencyName }} ({{ c.currencyCode }})
                    </option>
                  </select>
                </div>

                <div class="col-md-8">
                  <label class="form-label">Gateway Config</label>
                  <select class="form-select" formControlName="gatewayConfigId">
                    <option value="">Select gateway config</option>
                    <option *ngFor="let cfg of configs" [value]="cfg.id">
                      {{ cfg.providerName }} - {{ cfg.name }} ({{ cfg.environment }})
                    </option>
                  </select>
                </div>

                <div class="col-md-4">
                  <label class="form-label">Workflow</label>
                  <select class="form-select" formControlName="workflow">
                    <option *ngFor="let item of workflowOptions" [value]="item">{{ item }}</option>
                  </select>
                </div>

                <div class="col-md-4">
                  <label class="form-label">Payment Method</label>
                  <select class="form-select" formControlName="paymentMethod">
                    <option value="">Select method</option>
                    <option *ngFor="let item of paymentMethodOptions" [value]="item">{{ item }}</option>
                  </select>
                </div>

                <div class="col-md-4">
                  <label class="form-label">Frontend Hint</label>
                  <select class="form-select" formControlName="frontendHint">
                    <option value="">Select hint</option>
                    <option *ngFor="let item of frontendHintOptions" [value]="item">{{ item }}</option>
                  </select>
                </div>

                <div class="col-md-3">
                  <label class="form-label">Priority</label>
                  <input type="number" class="form-control" formControlName="priority" />
                </div>

                <div class="col-md-3">
                  <label class="form-label">Min Amount</label>
                  <input type="number" class="form-control" formControlName="minAmount" />
                </div>

                <div class="col-md-3">
                  <label class="form-label">Max Amount</label>
                  <input type="number" class="form-control" formControlName="maxAmount" />
                </div>

                <div class="col-md-3">
                  <label class="form-label">Country Code</label>
                  <select class="form-select" formControlName="countryCode">
                    <option value="">Select country</option>
                    <option *ngFor="let countryCode of countryOptions" [value]="countryCode">
                      {{ countryCode }}
                    </option>
                  </select>
                </div>

                <div class="col-md-6">
                  <label class="form-label">Customer Type</label>
                  <select class="form-select" formControlName="customerType">
                    <option value="">All</option>
                    <option *ngFor="let item of customerTypeOptions" [value]="item">{{ item }}</option>
                  </select>
                </div>

                <div class="col-md-6">
                  <label class="form-label">Metadata JSON</label>
                  <input type="text" class="form-control" formControlName="metadataJson" placeholder='{"key":"value"}' />
                </div>

                <div class="col-md-6">
                  <div class="form-check mt-2">
                    <input class="form-check-input" type="checkbox" formControlName="isDefault" id="isDefaultMapping" />
                    <label class="form-check-label" for="isDefaultMapping">Default Mapping</label>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-check mt-2">
                    <input class="form-check-input" type="checkbox" formControlName="isActive" id="isActiveMapping" />
                    <label class="form-check-label" for="isActiveMapping">Active</label>
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-footer">
              <button class="btn btn-outline-secondary" type="button" (click)="closeModal()">
                Cancel
              </button>

              <button class="btn btn-primary" type="submit" [disabled]="saving || form.invalid || !currencies.length">
                <span *ngIf="saving" class="spinner-border spinner-border-sm me-2"></span>
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <div class="modal-backdrop fade show" *ngIf="showModal"></div>

    <app-footer></app-footer>
  </main>
</div>
